import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronUp, ChevronDown, Building2, Info } from 'lucide-react';
import CalcSectionHeader from './CalcSectionHeader';
import AIButton from './AIButton';

const OperationalCosts = ({
 showOpEx,
 setShowOpEx,
 formData,
 setFormData,
 aiEstimatedFields,
 openAIDialog
}) => {
 return (
 <Card className="border border-border rounded-2xl overflow-hidden">
 <CalcSectionHeader
 icon={Building2}
 title="Operational Costs (OpEx)"
 description="Monthly recurring expenses — your business burn rate"
 expanded={showOpEx}
 onToggle={() => setShowOpEx(!showOpEx)}
 testId="section-header-opex"
 />
 {showOpEx && (
 <CardContent className="space-y-6 pt-5">
 {/* Facility & Utilities */}
 <div className="bg-gradient-to-r from-blue-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
 <div className="flex items-center gap-2 mb-3">
 <div className="bg-blue-500 text-white p-1.5 rounded">
 <Building2 className="w-4 h-4" />
 </div>
 <p className="text-sm font-bold text-blue-900 dark:text-blue-100">Facility & Utilities</p>
 </div>
 
 <div className="space-y-3">
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_rent" label="Rent/Space Cost ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Commercial space, home office allocation, or shared kitchen rental
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_utilities" label="Electricity ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Baseline facility electricity (beyond per-batch usage)
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_water" label="Water ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Cleaning, prep, and facility water usage
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_gas" label="Gas/Heating ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 HVAC, heating, or gas appliances if applicable
 </p>
 </div>
 </div>
 </div>

 {/* Insurance & Protection */}
 <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800 space-y-3">
 <p className="text-sm font-bold text-green-900 dark:text-green-100">🛡️ Insurance & Protection</p>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_insurance" label="Business Insurance ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 General liability, product liability, property insurance
 </p>
 </div>
 </div>

 {/* Services & Marketing */}
 <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800 space-y-3">
 <p className="text-sm font-bold text-orange-900 dark:text-orange-100">📢 Services & Marketing</p>
 
 <div className="space-y-3">
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_software" label="Software/Subscriptions ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 POS systems, accounting, inventory management, e-commerce platforms
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_marketing" label="Marketing & Advertising ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Social media ads, Google ads, influencer partnerships, samples
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_professional_services" label="Professional Services ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Accountant, lawyer, bookkeeper, consultant fees
 </p>
 </div>
 </div>
 </div>

 {/* Maintenance & Supplies */}
 <div className="bg-pro-soft dark:/20 dark:/20 p-4 rounded-lg border border-primary/30 dark:border-primary/30 space-y-3">
 <p className="text-sm font-bold text-primary dark:text-primary">🔧 Maintenance & Supplies</p>
 
 <div className="space-y-3">
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_maintenance" label="Equipment Maintenance ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Freeze dryer servicing, pump oil changes, filter replacements
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="monthly_supplies" label="General Supplies ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Office supplies, cleaning products, safety equipment
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="other_overhead" label="Other OpEx ($/mo)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Miscellaneous recurring costs not covered above
 </p>
 </div>
 </div>
 </div>

 {/* Batches per Month */}
 <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border-2 border-slate-300 dark:border-slate-700">
 <div className="space-y-2">
 <Label className="font-semibold flex items-center gap-2">
 Expected Batches per Month *
 <Info className="w-4 h-4 text-blue-500" />
 </Label>
 <Input
 type="number"
 value={formData.batches_per_month}
 onChange={(e) => setFormData(prev => ({ ...prev, batches_per_month: e.target.value }))}
 placeholder="20"
 />
 <p className="text-xs text-muted-foreground">
 💡 Used to calculate overhead cost per batch. Higher volume = lower per-batch OpEx
 </p>
 </div>
 </div>
 </CardContent>
 )}
 </Card>
 );
};

export default OperationalCosts;
